import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getConfiguration } from "../../clients/configuration"
import { GlobalOptOutsApi, TopicOptOut, TopicOptOutsApi } from "../../clients"
import { useCallback, useEffect, useState } from "react"
import { AppAlert } from "../AppAlerts/AppAlerts"
import { addAlertWithTimeout } from "../../redux/features/alerts/alertsHelpers"

export const MyAccountEmailPreferences = () => {

    const dispatch = useAppDispatch()
    const user = useAppSelector(state => state.currentUser.account)
    const [subscribedToClubhouse, setSubscribedToClubhouse] = useState<boolean>(true)
    const [isGlobalOptOut, setIsGlobalOptOut] = useState<boolean>(false)
    
    const loadEmailPreferences = useCallback(async () => {
        const configuration = await getConfiguration()
        const topicOptOutApi = new TopicOptOutsApi(configuration)
        const globalOptOutApi = new GlobalOptOutsApi(configuration)
        const [topicOptOuts, globalOptOuts] = await Promise.all([
            topicOptOutApi.listTopicOptOuts({
                endpoint: user.email,
            }),
            globalOptOutApi.checkEmailOptedOut({
                endpoint: user.email,
            })
        ])
        setSubscribedToClubhouse(!(topicOptOuts.data.some((topicOptOut: TopicOptOut) => topicOptOut.topic === 'clubhouse_reservation_updates')))
        setIsGlobalOptOut(globalOptOuts.data)
    }, [user.email])

    const handleSubscribeToClubhouseReservations = async () => {
        const configuration = await getConfiguration()
        const topicOptOutApi = new TopicOptOutsApi(configuration)
        await topicOptOutApi.deleteEmailTopicOptOut({
            endpoint: user.email,
            topic: 'clubhouse_reservation_updates',
        }).then(() => {
            const alert = new AppAlert('success', 'You have successfully subscribed to Clubhouse Reservation Updates')
            addAlertWithTimeout(dispatch, alert)
        }).catch(() => {
            const alert = new AppAlert('error', 'There was an error subscribing to Clubhouse Reservation Updates')
            setSubscribedToClubhouse(false)
            addAlertWithTimeout(dispatch, alert)
        })
    }

    const handleUnsubscribeFromClubhouseReservations = async () => {
        const configuration = await getConfiguration()
        const topicOptOutApi = new TopicOptOutsApi(configuration)
        await topicOptOutApi.createEmailTopicOptOut({
            endpoint: user.email,
            topic: 'clubhouse_reservation_updates',
        }).then(() => {
            const alert = new AppAlert('info', 'You have successfully unsubscribed from Clubhouse Reservation Updates')
            addAlertWithTimeout(dispatch, alert)
        }).catch(() => {
            const alert = new AppAlert('error', 'There was an error unsubscribing from Clubhouse Reservation Updates')
            setSubscribedToClubhouse(true)
            addAlertWithTimeout(dispatch, alert)
        })
    }

    const handleClubhouseReservationChange = (_, checked: boolean) => {
        setSubscribedToClubhouse(checked)
        if (checked) {
            handleSubscribeToClubhouseReservations()
        } else {
            handleUnsubscribeFromClubhouseReservations()
        }
    }

    const handleGlobalOptOutChange = (_, checked: boolean) => {
        setIsGlobalOptOut(checked)
        if (checked) {
            handleGlobalUnsubscribeChange()
        } else {
            handleGlobalSubscribeChange()
        }
    }

    const handleGlobalSubscribeChange = async () => {
        const configuration = await getConfiguration()
        const globalOptOutsApi = new GlobalOptOutsApi(configuration)
        await globalOptOutsApi.deleteEmailOptOut({
            endpoint: user.email,
        }).then(() => {
            const alert = new AppAlert('info', 'You have successfully subscribed to email communications')
            addAlertWithTimeout(dispatch, alert)
        }).catch(() => {
            const alert = new AppAlert('error', 'There was an error subscribing to email communications')
            setIsGlobalOptOut(true)
            addAlertWithTimeout(dispatch, alert)
        })
    }

    const handleGlobalUnsubscribeChange = async () => {
        const configuration = await getConfiguration()
        const globalOptOutsApi = new GlobalOptOutsApi(configuration)
        await globalOptOutsApi.createEmailOptOut({
            endpoint: user.email,
        }).then(() => {
            const alert = new AppAlert('info', 'You have successfully unsubscribed from all email communications')
            addAlertWithTimeout(dispatch, alert)
        }).catch(() => {
            const alert = new AppAlert('error', 'There was an error unsubscribing from email communications')
            setIsGlobalOptOut(false)
            addAlertWithTimeout(dispatch, alert)
        })
    }

    useEffect(() => {
        loadEmailPreferences()
    }, [loadEmailPreferences])

    return (
        <Box>
            <FormGroup>
                <FormControlLabel control={<Checkbox disabled={isGlobalOptOut} checked={isGlobalOptOut ? false : subscribedToClubhouse} onChange={handleClubhouseReservationChange} />} label='Clubhouse Reservation Updates' />
            </FormGroup>
            <Typography variant='caption' sx={{ ml: { xs: 0, sm: 3 }}}>
                Receive email updates about clubhouse reservation requests
            </Typography>
            <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel control={<Checkbox checked={isGlobalOptOut} onChange={handleGlobalOptOutChange} />} label='Unsubscribe from All' />
            </FormGroup>
        </Box>
    )
}